import { useEffect, useState } from "react";
import toast from "../../utils/toast.js";
import {
  addCar,
  updateCar,
  getDefaultSeatLayout,
  setCarSeats,
  addRoute,
  updateRoute,
  getRoutesByCarId,
  publishCar,
} from "../../services/carsService.js";

const VEHICLE_TYPES = {
  sedan: {
    carType: "5-seater",
    label: "5-Seater",
    subLabel: "Sedan · Dzire, Aura",
    sellableText: "4 sellable seats",
  },
  muv: {
    carType: "7-seater",
    label: "7-Seater",
    subLabel: "MUV · Ertiga, Innova",
    sellableText: "6 sellable seats",
  },
};

const CITIES = ["Lucknow", "Kanpur", "Delhi", "Agra", "Jaipur", "Varanasi", "Prayagraj", "Noida"];

const CAR_TYPE_TO_KEY = { "5-seater": "sedan", "7-seater": "muv" };

// Groups seats by their backend "position" field into front-to-back rows,
// purely for layout — the row is just seats.position, nothing invented.
const POSITION_ROW_ORDER = ["driver", "front", "middle", "rear"];
function groupSeatsIntoRows(seats) {
  return POSITION_ROW_ORDER.map((position) => seats.filter((s) => s.position === position)).filter(
    (row) => row.length > 0,
  );
}

// car: pass an existing car object to edit it; omit/null to add a new one.
// initialStep: which step to open on (e.g. from GET /cars/:id/status's resumeStep).
export default function AddCarWizard({ isOpen, onClose, car, initialStep }) {
  const isEditing = !!car;

  const [step, setStep] = useState(1);
  const [savingStep1, setSavingStep1] = useState(false);
  const [savingStep2, setSavingStep2] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [loadingRoute, setLoadingRoute] = useState(false);

  const [carId, setCarId] = useState(null);
  const [vehicleType, setVehicleType] = useState("sedan");
  const [carModel, setCarModel] = useState("");
  const [regNo, setRegNo] = useState("");
  const [fuelType, setFuelType] = useState("Petrol");
  const [driverName, setDriverName] = useState("");
  const [driverMobile, setDriverMobile] = useState("");
  const [step1Error, setStep1Error] = useState("");

  // Seats come straight from the backend (PUT /cars/:id/seats) — rendered
  // exactly as returned, nothing generated on the frontend.
  const [seats, setSeats] = useState([]);

  const [routeExists, setRouteExists] = useState(false);
  const [existingRouteId, setExistingRouteId] = useState(null);
  const [routeFrom, setRouteFrom] = useState("");
  const [routeTo, setRouteTo] = useState("");
  const [departures, setDepartures] = useState(["06:30", "14:00"]);
  const [newDepartureTime, setNewDepartureTime] = useState("");
  const [specialTrips, setSpecialTrips] = useState([]);
  const [newTripDate, setNewTripDate] = useState("");
  const [newTripTime, setNewTripTime] = useState("");
  const [rateFront, setRateFront] = useState("449");
  const [rateMiddle, setRateMiddle] = useState("349");
  const [rateRear, setRateRear] = useState("");
  const [rateFull, setRateFull] = useState("1899");
  const [allowPerSeat, setAllowPerSeat] = useState(true);
  const [allowFullCab, setAllowFullCab] = useState(true);
  const [autoConfirm, setAutoConfirm] = useState(true);
  const [step3Error, setStep3Error] = useState("");

  useEffect(() => {
    if (isOpen) resetWizard();
  }, [isOpen, car, initialStep]);

  function resetWizard() {
    setStep(initialStep || 1);
    setSavingStep1(false);
    setPublishing(false);
    setStep1Error("");
    setStep3Error("");
    setRouteExists(false);
    setExistingRouteId(null);
    setNewTripDate("");
    setNewTripTime("");

    if (car) {
      setCarId(car._id);
      setVehicleType(CAR_TYPE_TO_KEY[car.carType] || "sedan");
      setCarModel(car.carModel || "");
      setRegNo(car.registrationNo || "");
      setFuelType(car.fuelType || "Petrol");
      setDriverName(car.driverName || "");
      setDriverMobile(car.driverMobile || "");
      setSeats(car.seats || []);
      setSpecialTrips([]);
      loadExistingRoute(car._id);
    } else {
      setCarId(null);
      setVehicleType("sedan");
      setCarModel("");
      setRegNo("");
      setFuelType("Petrol");
      setDriverName("");
      setDriverMobile("");
      setSeats([]);
      setRouteFrom("");
      setRouteTo("");
      setDepartures(["06:30", "14:00"]);
      setNewDepartureTime("");
      setSpecialTrips([]);
      setRateFront("449");
      setRateMiddle("349");
      setRateRear("");
      setRateFull("1899");
      setAllowPerSeat(true);
      setAllowFullCab(true);
      setAutoConfirm(true);
    }
  }

  // Used only for a draft car that hasn't got its first route yet — at this
  // point a car can have at most one route, so the first one is "the" route.
  async function loadExistingRoute(id) {
    setLoadingRoute(true);
    try {
      const res = await getRoutesByCarId(id);
      const route = res.success && Array.isArray(res.data) ? res.data[0] : null;
      if (route) {
        setRouteExists(true);
        setExistingRouteId(route._id);
        setRouteFrom(route.fromCity || "");
        setRouteTo(route.toCity || "");
        setDepartures(Array.isArray(route.dailyDepartures) ? route.dailyDepartures : []);
        setSpecialTrips(Array.isArray(route.specialTrips) ? route.specialTrips : []);
        setRateFront(String(route.rateCard?.front ?? "449"));
        setRateMiddle(String(route.rateCard?.middle ?? "349"));
        setRateRear(route.rateCard?.rear ? String(route.rateCard.rear) : "");
        setRateFull(String(route.rateCard?.fullCab ?? "1899"));
        setAllowPerSeat(route.allowPerSeatSharing ?? true);
        setAllowFullCab(route.allowFullCabBooking ?? true);
        setAutoConfirm(route.autoConfirmBookings ?? true);
      }
    } catch {
      // No route yet for this car — leave the defaults, addRoute will run instead of updateRoute.
      setRouteExists(false);
    } finally {
      setLoadingRoute(false);
    }
  }

  function addDeparture() {
    if (!newDepartureTime || departures.includes(newDepartureTime)) return;
    setDepartures((prev) => [...prev, newDepartureTime].sort());
    setNewDepartureTime("");
  }

  function removeDeparture(time) {
    setDepartures((prev) => prev.filter((t) => t !== time));
  }

  function addSpecialTrip() {
    if (!newTripDate || !newTripTime) return;
    const exists = specialTrips.some((t) => t.date === newTripDate && t.time === newTripTime);
    if (exists) return;
    setSpecialTrips((prev) =>
      [...prev, { date: newTripDate, time: newTripTime }].sort((a, b) =>
        a.date === b.date ? a.time.localeCompare(b.time) : a.date.localeCompare(b.date),
      ),
    );
    setNewTripDate("");
    setNewTripTime("");
  }

  function removeSpecialTrip(date, time) {
    setSpecialTrips((prev) => prev.filter((t) => !(t.date === date && t.time === time)));
  }

  function formatTripPillLabel(date, time) {
    const d = new Date(`${date}T00:00:00`);
    const dateLabel = d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
    return `${dateLabel}, ${time}`;
  }

  function toggleSeat(seatNumber) {
    setSeats((prev) =>
      prev.map((s) =>
        s.seatNumber === seatNumber && s.position !== "driver"
          ? { ...s, isSellable: !s.isSellable }
          : s,
      ),
    );
  }

  // Step 1 → 2: create or update the car, then fetch its backend-generated
  // seat layout (PUT /cars/:id/seats).
  async function handleStep1Next() {
    if (
      !carModel.trim() ||
      !regNo.trim() ||
      !driverName.trim() ||
      !/^\d{10}$/.test(driverMobile.trim())
    ) {
      setStep1Error("Please fill in all car & driver fields (driver mobile must be 10 digits).");
      return;
    }
    setStep1Error("");
    setSavingStep1(true);
    try {
      const carPayload = {
        carType: VEHICLE_TYPES[vehicleType].carType,
        carModel: carModel.trim(),
        registrationNo: regNo.trim(),
        fuelType,
        driverName: driverName.trim(),
        driverMobile: driverMobile.trim(),
      };

      let activeCarId = carId;
      if (isEditing) {
        const res = await updateCar(activeCarId, carPayload);
        if (!res.success) {
          toast.error(res.message );
          return;
        }
      } else if (!activeCarId) {
        const res = await addCar(carPayload);
        if (!res.success) {
          toast.error(res.message );
          return;
        }
        activeCarId = res.data._id;
        setCarId(activeCarId);
      }

      // For a new/not-yet-laid-out car, fetch a fresh default layout to display.
      // For a car being edited that already has saved seats, keep those as-is.
      if (!isEditing || !car?.seats?.length) {
        const seatsRes = await getDefaultSeatLayout(activeCarId);
        if (!seatsRes.success) {
          toast.error(seatsRes.message);
          return;
        }
        const freshSeats = Array.isArray(seatsRes.data) ? seatsRes.data : seatsRes.data?.seats;
        setSeats(freshSeats || []);
      }
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setSavingStep1(false);
    }
  }

  // Step 2 → 3: persist the (possibly toggled) seat layout to the backend.
  async function handleStep2Next() {
    setSavingStep2(true);
    try {
      const res = await setCarSeats(carId, seats);
      if (!res.success) {
        toast.error(res.message);
        return;
      }
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.message || "Couldn't save the seat layout.");
    } finally {
      setSavingStep2(false);
    }
  }

  async function handlePublish(e) {
    e.preventDefault();
    if (!routeFrom || !routeTo || routeFrom === routeTo) {
      setStep3Error("Please select two different cities for the route.");
      return;
    }
    if (!departures.length && !specialTrips.length) {
      setStep3Error("Please add at least one daily departure or a one-time special trip.");
      return;
    }
    if (
      !rateFront ||
      !rateMiddle ||
      (vehicleType === "muv" && !rateRear) ||
      !rateFull
    ) {
      setStep3Error("Please fill in every seat-tier price.");
      return;
    }
    setStep3Error("");
    setPublishing(true);

    try {
      const routePayload = {
        fromCity: routeFrom,
        toCity: routeTo,
        dailyDepartures: departures,
        specialTrips,
        rateCard: {
          front: Number(rateFront),
          middle: Number(rateMiddle),
          rear: vehicleType === "muv" ? Number(rateRear) : 0,
          fullCab: Number(rateFull),
        },
        allowPerSeatSharing: allowPerSeat,
        allowFullCabBooking: allowFullCab,
        autoConfirmBookings: autoConfirm,
      };

      const routeRes = routeExists
        ? await updateRoute(existingRouteId, routePayload)
        : await addRoute(carId, routePayload);
      if (!routeRes.success) {
        toast.error(routeRes.message );
        return;
      }

      const publishRes = await publishCar(carId);
      if (!publishRes.success) {
        toast.error(publishRes.message );
        return;
      }

      toast.success(`${carModel} published! It's now live for booking.`);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setPublishing(false);
    }
  }

  if (!isOpen) return null;

  const today = new Date().toISOString().split("T")[0];
  const sellableCount = seats.filter((s) => s.isSellable).length;
  const totalSeats = seats.length;

  return (
    <div className="wizard-overlay open">
      <div className="wizard-modal">
        <button type="button" className="wizard-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        <button type="button" className="wizard-back-link" onClick={onClose}>
          ← Back to fleet
        </button>

        <div className="wizard-steps">
          <div className={`wizard-step-pill ${step === 1 ? "active" : step > 1 ? "done" : ""}`}>
            <span>1</span> Car
          </div>
          <div className={`wizard-step-pill ${step === 2 ? "active" : step > 2 ? "done" : ""}`}>
            <span>2</span> Layout
          </div>
          <div className={`wizard-step-pill ${step === 3 ? "active" : ""}`}>
            <span>3</span> Route & Rates
          </div>
        </div>

        {step === 1 && (
          <div className="wizard-panel active">
            <h2>{isEditing ? "Edit car" : "Car details"}</h2>
            <p className="wizard-sub">Basic identity of the vehicle you're putting on a route.</p>

            <div className="vehicle-type-grid">
              {Object.entries(VEHICLE_TYPES).map(([key, type]) => (
                <label
                  key={key}
                  className={`vehicle-type-card ${vehicleType === key ? "selected" : ""}`}
                >
                  <input
                    type="radio"
                    name="vehicleType"
                    value={key}
                    checked={vehicleType === key}
                    disabled={!isEditing && !!carId}
                    onChange={() => setVehicleType(key)}
                  />
                  <strong>{type.label}</strong>
                  <span>{type.subLabel}</span>
                  <em>{type.sellableText}</em>
                </label>
              ))}
            </div>

            <div className="field">
              <label htmlFor="carModel">Car model name</label>
              <input
                type="text"
                id="carModel"
                placeholder="e.g. Swift Dzire VXi"
                value={carModel}
                onChange={(e) => setCarModel(e.target.value)}
              />
            </div>
            <div className="field-row-2">
              <div className="field">
                <label htmlFor="regNo">Registration no.</label>
                <input
                  type="text"
                  id="regNo"
                  placeholder="UP32 KT 7788"
                  value={regNo}
                  onChange={(e) => setRegNo(e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="fuelType">Fuel</label>
                <select
                  id="fuelType"
                  value={fuelType}
                  onChange={(e) => setFuelType(e.target.value)}
                >
                  <option>Petrol</option>
                  <option>Diesel</option>
                  <option>CNG</option>
                  <option>EV</option>
                </select>
              </div>
            </div>
            <div className="field-row-2">
              <div className="field">
                <label htmlFor="driverName">Driver name</label>
                <input
                  type="text"
                  id="driverName"
                  placeholder="Ramesh Yadav"
                  value={driverName}
                  onChange={(e) => setDriverName(e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="driverMobile">Driver mobile</label>
                <input
                  type="tel"
                  id="driverMobile"
                  placeholder="98765 43210"
                  inputMode="numeric"
                  maxLength={10}
                  value={driverMobile}
                  onChange={(e) => setDriverMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                />
              </div>
            </div>
            <p className="field-error">{step1Error}</p>

            <div className="wizard-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleStep1Next}
                disabled={savingStep1}
              >
                {savingStep1 ? "Saving..." : "Next: Adjust Seat Layout →"}
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="wizard-panel active">
            <h2>Adjust seat layout</h2>
            <p className="wizard-sub">
              Layout auto-generated from car type. Tap a seat to block/unblock it from sale
              (e.g. keep the middle row empty for comfort).
            </p>
            <p className="wizard-hint">TAP SEATS TO TOGGLE SELLABLE ⇄ BLOCKED</p>

            <div className="seat-map">
              {groupSeatsIntoRows(seats).map((row, i) => (
                <div className="seat-row" key={i}>
                  {row.map((seat) =>
                    seat.position === "driver" ? (
                      <div className="seat-btn driver" key={seat.seatNumber} title="Driver seat">
                        {seat.seatNumber}
                      </div>
                    ) : (
                      <button
                        type="button"
                        key={seat.seatNumber}
                        className={`seat-btn ${seat.isSellable ? "sellable" : "blocked"}`}
                        onClick={() => toggleSeat(seat.seatNumber)}
                        title={
                          seat.isSellable
                            ? "Sellable — tap to block"
                            : "Blocked — tap to make sellable"
                        }
                      >
                        {seat.seatNumber}
                      </button>
                    ),
                  )}
                </div>
              ))}
            </div>

            <div className="seat-legend">
              <span>
                <span className="seat-swatch sellable" /> Sellable
              </span>
              <span>
                <span className="seat-swatch blocked" /> Blocked
              </span>
              <span>
                <span className="seat-swatch driver" /> Driver
              </span>
            </div>

            <p className="wizard-hint" style={{ textAlign: "center" }}>
              {sellableCount} of {totalSeats} seats sellable — blocked seats never show to
              customers.
            </p>

            <div className="wizard-actions">
              <button type="button" className="btn btn-outline" onClick={() => setStep(1)}>
                ← Back
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleStep2Next}
                disabled={savingStep2}
              >
                {savingStep2 ? "Saving..." : "Next: Route & Rate Card →"}
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="wizard-panel active">
            <h2>Route & rate card</h2>
            <p className="wizard-sub">
              Fix the route this car runs on, its departures, and the price of every seat tier.
            </p>
            {loadingRoute && <p className="wizard-hint">Loading existing route...</p>}

            <div className="field-row-2">
              <div className="field">
                <label htmlFor="routeFrom">Route from</label>
                <select
                  id="routeFrom"
                  value={routeFrom}
                  onChange={(e) => setRouteFrom(e.target.value)}
                >
                  <option value="">Select city</option>
                  {CITIES.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="routeTo">Route to</label>
                <select id="routeTo" value={routeTo} onChange={(e) => setRouteTo(e.target.value)}>
                  <option value="">Select city</option>
                  {CITIES.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="field">
              <label htmlFor="newDepartureTime">Daily departures</label>
              <div className="field-row-2">
                <input
                  type="time"
                  id="newDepartureTime"
                  value={newDepartureTime}
                  onChange={(e) => setNewDepartureTime(e.target.value)}
                />
                <button type="button" className="btn btn-outline" onClick={addDeparture}>
                  + Add time
                </button>
              </div>
              <div className="time-pills">
                {departures.map((time) => (
                  <button
                    type="button"
                    key={time}
                    className="time-pill selected"
                    onClick={() => removeDeparture(time)}
                    title="Tap to remove"
                  >
                    {time} ×
                  </button>
                ))}
              </div>
            </div>

            <div className="field">
              <label htmlFor="newTripDate">One-time special trips</label>
              <div className="field-row-2">
                <input
                  type="date"
                  id="newTripDate"
                  min={today}
                  value={newTripDate}
                  onChange={(e) => setNewTripDate(e.target.value)}
                />
                <input
                  type="time"
                  id="newTripTime"
                  value={newTripTime}
                  onChange={(e) => setNewTripTime(e.target.value)}
                />
              </div>
              <button type="button" className="btn btn-outline" onClick={addSpecialTrip}>
                + Add trip
              </button>
              <div className="time-pills">
                {specialTrips.map((trip) => (
                  <button
                    type="button"
                    key={`${trip.date}-${trip.time}`}
                    className="time-pill selected"
                    onClick={() => removeSpecialTrip(trip.date, trip.time)}
                    title="Tap to remove"
                  >
                    {formatTripPillLabel(trip.date, trip.time)} ×
                  </button>
                ))}
              </div>
            </div>

            <h3 className="rate-card-heading">Per-seat rate card (₹ / seat)</h3>
            <div className={`rate-row ${!allowPerSeat ? "disabled" : ""}`}>
              <div>
                <strong>Front (co-driver)</strong>
                <span>Best view · 1 seat</span>
              </div>
              <div className="rate-input">
                <span>₹</span>
                <input
                  type="number"
                  value={rateFront}
                  onChange={(e) => setRateFront(e.target.value)}
                />
              </div>
            </div>
            <div className={`rate-row ${!allowPerSeat ? "disabled" : ""}`}>
              <div>
                <strong>Middle row</strong>
                <span>Window & middle seats</span>
              </div>
              <div className="rate-input">
                <span>₹</span>
                <input
                  type="number"
                  value={rateMiddle}
                  onChange={(e) => setRateMiddle(e.target.value)}
                />
              </div>
            </div>
            {vehicleType === "muv" && (
              <div className={`rate-row ${!allowPerSeat ? "disabled" : ""}`}>
                <div>
                  <strong>Rear row</strong>
                  <span>Bench seating</span>
                </div>
                <div className="rate-input">
                  <span>₹</span>
                  <input
                    type="number"
                    value={rateRear}
                    onChange={(e) => setRateRear(e.target.value)}
                  />
                </div>
              </div>
            )}
            <div className={`rate-row ${!allowFullCab ? "disabled" : ""}`}>
              <div>
                <strong>Full cab (exclusive)</strong>
                <span>Entire car, door-to-door</span>
              </div>
              <div className="rate-input">
                <span>₹</span>
                <input
                  type="number"
                  value={rateFull}
                  onChange={(e) => setRateFull(e.target.value)}
                />
              </div>
            </div>

            <div className="toggle-row">
              <strong>Allow per-seat sharing</strong>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={allowPerSeat}
                  onChange={(e) => setAllowPerSeat(e.target.checked)}
                />
                <span className="toggle-switch-track" />
              </label>
            </div>
            <div className="toggle-row">
              <strong>Allow full cab booking</strong>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={allowFullCab}
                  onChange={(e) => setAllowFullCab(e.target.checked)}
                />
                <span className="toggle-switch-track" />
              </label>
            </div>
            <div className="toggle-row">
              <strong>Auto-confirm bookings</strong>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={autoConfirm}
                  onChange={(e) => setAutoConfirm(e.target.checked)}
                />
                <span className="toggle-switch-track" />
              </label>
            </div>

            <div className="rate-preview">
              <strong>Rate card preview</strong>
              <div className="rate-preview-row">
                <span>Route</span>
                <span>{routeFrom && routeTo ? `${routeFrom} → ${routeTo}` : "—"}</span>
              </div>
              <div className="rate-preview-row">
                <span>Departures</span>
                <span>{departures.length ? departures.join(", ") : "—"}</span>
              </div>
              <div className="rate-preview-row">
                <span>Special trips</span>
                <span>{specialTrips.length ? `${specialTrips.length} added` : "—"}</span>
              </div>
              <div className="rate-preview-row">
                <span>Front seat</span>
                <span>₹{rateFront || 0}</span>
              </div>
              <div className="rate-preview-row">
                <span>Middle seats</span>
                <span>₹{rateMiddle || 0}</span>
              </div>
              {vehicleType === "muv" && (
                <div className="rate-preview-row">
                  <span>Rear seats</span>
                  <span>₹{rateRear || 0}</span>
                </div>
              )}
              <div className="rate-preview-row">
                <span>Full cab</span>
                <span>₹{rateFull || 0}</span>
              </div>
            </div>
            <p className="field-error">{step3Error}</p>

            <div className="wizard-actions">
              <button type="button" className="btn btn-outline" onClick={() => setStep(2)}>
                ← Back
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handlePublish}
                disabled={publishing}
              >
                {publishing
                  ? "Saving..."
                  : car?.isPublished
                  ? "Save Changes ✓"
                  : "Publish Car ✓"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
